<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import * as yup from 'yup';
import { passwordsApi, type PasswordScope } from '@/api/passwords';
import type { AxiosError } from 'axios';

const props = withDefaults(defineProps<{ scope?: PasswordScope }>(), { scope: 'caregiver' });

const { t } = useI18n();

const HTTP_TOO_MANY_REQUESTS = 429;

const validationSchema = yup.object({
  email: yup.string().email(t('recoverPassword.emailInvalid')).required(t('recoverPassword.emailRequired')),
});

const { handleSubmit, values, errors, defineField } = useForm({ validationSchema });
const [email, emailAttrs] = defineField('email');

const { mutate: recoverPassword, isError, error, isSuccess, isPending } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => passwordsApi(props.scope).create(values.email),
  },
);

const onSubmit = handleSubmit(() => recoverPassword());

// The endpoint answers 200 whether or not the email exists, so the only errors left are
// the rack-attack throttle and network/server failures.
const errorText = computed(() => {
  if (!isError.value) return '';

  return error.value?.response?.status === HTTP_TOO_MANY_REQUESTS ?
    t('recoverPassword.tooManyRequests') :
    t('recoverPassword.emailError');
});
</script>
<template>
  <div>
    <div
      v-if="isSuccess"
      class="justify-center d-flex align-center"
      style="min-height: 60vh;"
    >
      <v-card
        class="text-center pa-8"
        max-width="500"
      >
        <v-icon
          color="success"
          size="80"
          class="mb-4"
        >
          mdi-check-circle
        </v-icon>
        <h2 class="mb-4 text-h4 font-weight-bold text-success">
          {{ t('passwordRecoverySuccess.title') }}
        </h2>
        <p class="mb-6 text-body-1 text-grey-darken-1">
          {{ t('passwordRecoverySuccess.message') }}
        </p>
        <p class="text-body-2 text-grey">
          {{ t('passwordRecoverySuccess.closeTab') }}
        </p>
      </v-card>
    </div>
    <div
      v-else
    >
      <h3 class="mb-2 text-2xl font-semibold">
        {{ t('recoverPassword.title') }}
      </h3>
      <p class="mb-8 text-gray-400">
        {{ t('recoverPassword.subtitle') }}
      </p>
      <v-form @submit.prevent="onSubmit">
        <v-text-field
          v-model="email"
          v-bind="emailAttrs"
          variant="outlined"
          :label="t('recoverPassword.email')"
          :error-messages="errors.email"
        />
        <v-btn
          color="primary"
          type="submit"
          :loading="isPending"
        >
          {{ t('recoverPassword.submit') }}
        </v-btn>
      </v-form>
      <v-alert
        v-if="isError"
        class="mt-10 rounded"
        :type="'error'"
      >
        {{ errorText || t('userSession.defaultError') }}
      </v-alert>
    </div>
  </div>
</template>
